<template>
  <v-dialog
    :value="value"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
    content-class="fullscreen-viewer-dialog"
    persistent
    @input="$emit('input', $event)"
  >
    <div
      class="fullscreen-viewer-backdrop"
      @click.self="close"
    >
      <div
        ref="viewerRoot"
        class="fullscreen-viewer"
        tabindex="-1"
        @wheel.prevent="onWheel"
        @click.stop
      >
      <!-- Toolbar -->
      <div class="fullscreen-viewer__toolbar">
        <div class="fullscreen-viewer__zoom-controls">
          <v-btn
            icon
            :disabled="scale <= minScale"
            @click="zoomOut"
          >
            <v-icon>mdi-magnify-minus-outline</v-icon>
          </v-btn>
          <span class="fullscreen-viewer__zoom-label">{{ Math.round(scale * 100) }}%</span>
          <v-btn
            icon
            :disabled="scale >= maxScale"
            @click="zoomIn"
          >
            <v-icon>mdi-magnify-plus-outline</v-icon>
          </v-btn>
        </div>
        <div class="fullscreen-viewer__toolbar-actions">
          <v-btn
            v-if="deletableNanoBananaFilename"
            icon
            class="fullscreen-viewer__delete"
            title="Delete generated file"
            aria-label="Delete generated file"
            :loading="deleteLoading"
            :disabled="deleteLoading || downloadLoading"
            @click="deleteNanoBananaFile"
          >
            <v-icon>mdi-delete-outline</v-icon>
          </v-btn>
          <v-btn
            icon
            class="fullscreen-viewer__download"
            title="Download"
            aria-label="Download"
            :loading="downloadLoading"
            :disabled="downloadLoading"
            @click="downloadImage"
          >
            <v-icon>mdi-download-outline</v-icon>
          </v-btn>
          <v-btn
            icon
            class="fullscreen-viewer__close"
            aria-label="Close"
            @click="close"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Image container (pannable when zoomed) -->
      <div
        class="fullscreen-viewer__stage"
        @mousedown="onStageMouseDown"
        @mousemove="onStageMouseMove"
        @mouseup="onStageMouseUp"
        @mouseleave="onStageMouseUp"
      >
        <div
          class="fullscreen-viewer__content"
          :style="contentStyle"
        >
          <img
            v-if="imageUrl"
            :src="imageUrl"
            class="fullscreen-viewer__img"
            :style="imgStyle"
            draggable="false"
            @load="onImageLoad"
          >
          <div
            v-else
            class="fullscreen-viewer__placeholder"
          >
            <v-icon size="80">
              mdi-image-off-outline
            </v-icon>
            <p>No image</p>
          </div>
        </div>
      </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { deleteNanoBananaImage } from '@/services/pdfGenerationService'

const MIN_SCALE = 0.25
const MAX_SCALE = 4
const SCALE_STEP = 0.25
const WHEEL_STEP = 0.1

export default {
  name: 'FullscreenImageViewer',
  props: {
    value: { type: Boolean, default: false },
    imageUrl: { type: String, default: null },
    /** Server filename for nanoBanana delete API; only set for generated/filtered edits. */
    deletableNanoBananaFilename: { type: String, default: null },
  },
  data() {
    return {
      scale: 1,
      translateX: 0,
      translateY: 0,
      dragStart: null,
      imageNaturalWidth: 0,
      imageNaturalHeight: 0,
      downloadLoading: false,
      deleteLoading: false,
    }
  },
  computed: {
    minScale() {
      return MIN_SCALE
    },
    maxScale() {
      return MAX_SCALE
    },
    contentStyle() {
      return {
        transform: `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`,
      }
    },
    imgStyle() {
      return {}
    },
  },
  watch: {
    value(open) {
      if (!open) {
        this.resetTransform()
        this.deleteLoading = false
        this.removeEscapeListener()
      } else {
        this.$nextTick(() => this.focusViewer())
        this.addEscapeListener()
      }
    },
    imageUrl() {
      this.resetTransform()
    },
  },
  beforeDestroy() {
    this.removeEscapeListener()
  },
  methods: {
    addEscapeListener() {
      this._escapeHandler = (e) => {
        if (e.key === 'Escape') {
          e.preventDefault()
          this.close()
        }
      }
      document.addEventListener('keydown', this._escapeHandler)
    },
    removeEscapeListener() {
      if (this._escapeHandler) {
        document.removeEventListener('keydown', this._escapeHandler)
        this._escapeHandler = null
      }
    },
    focusViewer() {
      if (this.$refs.viewerRoot) this.$refs.viewerRoot.focus()
    },
    async deleteNanoBananaFile() {
      const fn = this.deletableNanoBananaFilename
      const url = this.imageUrl
      if (!fn || !url || this.deleteLoading) return
      this.deleteLoading = true
      try {
        const res = await deleteNanoBananaImage(fn)
        if (res && res.status === 200) {
          this.$emit('nano-banana-deleted', { url })
          this.close()
        }
      } catch (err) {
        console.error('Delete nanoBanana image failed:', err)
      } finally {
        this.deleteLoading = false
      }
    },
    async downloadImage() {
      if (!this.imageUrl || this.downloadLoading) return
      this.downloadLoading = true
      try {
        const res = await fetch(this.imageUrl, { mode: 'cors' })
        const blob = await res.blob()
        let ext = (res.headers.get('content-type') || 'image/png').split('/')[1] || 'png'
        if (ext === 'svg+xml' || ext.startsWith('svg')) ext = 'svg'
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `image.${ext}`
        a.click()
        URL.revokeObjectURL(url)
      } catch (err) {
        const a = document.createElement('a')
        a.href = this.imageUrl
        a.download = 'image.png'
        a.target = '_blank'
        a.click()
      } finally {
        this.downloadLoading = false
      }
    },
    resetTransform() {
      this.scale = 1
      this.translateX = 0
      this.translateY = 0
    },
    zoomIn() {
      this.scale = Math.min(this.maxScale, this.scale + SCALE_STEP)
    },
    zoomOut() {
      this.scale = Math.max(this.minScale, this.scale - SCALE_STEP)
    },
    onWheel(e) {
      const delta = e.deltaY > 0 ? -WHEEL_STEP : WHEEL_STEP
      this.scale = Math.min(this.maxScale, Math.max(this.minScale, this.scale + delta))
    },
    onImageLoad(e) {
      const img = e.target
      this.imageNaturalWidth = img.naturalWidth
      this.imageNaturalHeight = img.naturalHeight
    },
    onStageMouseDown(e) {
      if (e.button !== 0) return
      this.dragStart = { x: e.clientX - this.translateX, y: e.clientY - this.translateY }
    },
    onStageMouseMove(e) {
      if (!this.dragStart) return
      this.translateX = e.clientX - this.dragStart.x
      this.translateY = e.clientY - this.dragStart.y
    },
    onStageMouseUp() {
      this.dragStart = null
    },
    close() {
      this.$emit('input', false)
    },
  },
}
</script>

<style scoped>
.fullscreen-viewer-dialog {
  margin: 0;
  padding: 0;
}

.fullscreen-viewer-backdrop {
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.75);
  padding: 24px;
  box-sizing: border-box;
}

.fullscreen-viewer {
  width: 100%;
  max-width: 1200px;
  height: 90vh;
  max-height: 860px;
  background: rgba(22, 22, 26, 0.98);
  display: flex;
  flex-direction: column;
  cursor: default;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.06);
  outline: none;
}

.fullscreen-viewer:focus {
  outline: none;
}

.fullscreen-viewer__toolbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: rgba(0, 0, 0, 0.4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.fullscreen-viewer__zoom-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.fullscreen-viewer__zoom-label {
  min-width: 52px;
  text-align: center;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
}

.fullscreen-viewer__toolbar-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.fullscreen-viewer__delete {
  color: #ef5350 !important;
}

.fullscreen-viewer__delete >>> .v-icon {
  color: inherit !important;
}

.fullscreen-viewer__download {
  color: rgba(255, 255, 255, 0.95) !important;
  text-transform: none;
  font-weight: 600;
}

.fullscreen-viewer__close {
  color: #fff !important;
  background: rgba(255, 255, 255, 0.1) !important;
}

.fullscreen-viewer__close:hover {
  background: #c62828 !important;
}

.fullscreen-viewer__stage {
  flex: 1;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
}

.fullscreen-viewer__stage:active {
  cursor: grabbing;
}

.fullscreen-viewer__content {
  transform-origin: center center;
  transition: transform 0.05s ease-out;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fullscreen-viewer__img {
  max-width: 90vw;
  max-height: 85vh;
  width: auto;
  height: auto;
  object-fit: contain;
  user-select: none;
  pointer-events: none;
}

.fullscreen-viewer__placeholder {
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
  padding: 24px;
}
</style>
